import React from "react";
import { usd, signed, tone } from "./floorUtil";

const Stat = ({ label, value, cls = "", testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <p className="fc-label">{label}</p>
    <p key={value} className={`fc-num fc-flash rounded font-bold text-base lg:text-xl leading-tight truncate ${cls}`}>{value}</p>
  </div>
);

const Pill = ({ label, on, value, testid }) => {
  const c = on ? "#2CF6B3" : "#FF3D68";
  return (
    <span key={`${on}-${value}`} data-testid={testid} data-on={on}
      className="fc-flash fc-num inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest border"
      style={{ color: c, borderColor: `${c}66`, background: `${c}12` }}>
      <span className={`w-1.5 h-1.5 rounded-full ${on ? "animate-pulse" : ""}`} style={{ background: c }} />
      {label}{value != null ? ` ${value}` : ""}
    </span>
  );
};

const pct = (v, base) => (v == null || !base ? null : (v / base) * 100);
const fp = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`);

export const floorStats = (pnl, equity, hidden) => {
  const day = pnl ? (pnl.day?.realized || 0) + (pnl.open?.unrealized || 0) : null;
  const dep = pnl?.open?.deployed;
  const dayPct = equity && day != null ? pct(day, equity - day) : null;
  return hidden ? [
    ["Equity · day", fp(dayPct), tone(dayPct), "floor-equity"],
    ["Day on deployed", fp(pct(day, dep)), tone(day), "floor-day-pnl"],
    ["Week", fp(pct(pnl?.week?.realized, equity)), tone(pnl?.week?.realized), "floor-week-pnl"],
    ["Month", fp(pct(pnl?.month?.realized, equity)), tone(pnl?.month?.realized), "floor-month-pnl"],
    ["Deployed", dep != null && equity ? `${pct(dep, equity).toFixed(1)}%` : "—", "text-cyan-50", "floor-deployed"],
  ] : [
    ["Equity", usd(equity), "text-cyan-50", "floor-equity"],
    ["Day P&L", signed(day), tone(day), "floor-day-pnl"],
    ["Week", signed(pnl?.week?.realized), tone(pnl?.week?.realized), "floor-week-pnl"],
    ["Month", signed(pnl?.month?.realized), tone(pnl?.month?.realized), "floor-month-pnl"],
    ["Open", pnl ? String(pnl.open?.positions ?? 0) : "—", "text-cyan-50", "floor-open-count"],
    ["Deployed", usd(dep), "text-cyan-50", "floor-deployed"],
  ];
};

export const Pills = ({ live, gates }) => (
  <>
    <Pill label="ARMED" on={!!live?.armed} testid="pill-armed" />
    <Pill label="AUTOPILOT" on={!!live?.autopilot} testid="pill-autopilot" />
    <Pill label="ENGINE" on={!!live?.engine_on} value={live?.engine_on ? "LIVE" : "OFF"} testid="pill-engine" />
    {gates && <Pill label="LONG ≥" on value={gates?.long ?? "—"} testid="pill-long-gate" />}
    {gates && <Pill label="SHORT ≥" on value={gates?.short ?? "—"} testid="pill-short-gate" />}
  </>
);

export const FloorHeader = ({ pnl, equity, live, gates, hidden, usdOn, onToggleUsd }) => {
  const stats = floorStats(pnl, equity, hidden);
  return (
    <header className="fc-panel px-4 py-3 space-y-3" data-testid="floor-header">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-x-6 gap-y-3 items-end">
        {stats.map(([l, v, c, t]) => <Stat key={t} label={l} value={v} cls={c} testid={t} />)}
      </div>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="floor-status-pills">
        {onToggleUsd && (
          <button onClick={onToggleUsd} data-testid="floor-usd-toggle" aria-pressed={usdOn} title={usdOn ? "Hide dollar amounts" : "Show dollar amounts"}
            className={`fc-num px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${usdOn ? "bg-[#00F0FF] text-[#030712] border-[#00F0FF]" : "text-cyan-200/50 border-cyan-200/25"}`}>
            {usdOn ? "$ ON" : "% ONLY"}
          </button>
        )}
        <Pills live={live} gates={gates} />
      </div>
    </header>
  );
};
